/**
 * Extracts a clean, human-readable message from a failed fetch Response.
 *
 * The backend's default error body is a JSON envelope, e.g.
 *   {"timestamp":"...","status":403,"error":"Forbidden","message":"Admin or Cashier only","path":"..."}
 * Reading `res.text()` and throwing it directly (the old pattern all over
 * this app) shows that whole JSON blob to the user. This pulls out just
 * the `message` so users only ever see plain text — falling back through
 * a few layers when `message` isn't usable:
 *   1. `message`, unless it's Spring's placeholder "No message available"
 *      (server.error.include-message is off in some environments).
 *   2. A ResponseStatusException reason recovered from the `trace` field,
 *      when the server included a stack trace.
 *   3. `error` (the HTTP reason phrase, e.g. "Forbidden").
 *   4. The raw response body, if it's short (already-plain-text errors).
 *   5. A generic "<fallback|Request> failed (<status>)" string.
 */
export async function extractErrorMessage(res, fallback) {
  const defaultMsg = fallback || `Request failed (${res.status})`;
  let raw;
  try {
    raw = await res.text();
  } catch {
    return defaultMsg;
  }
  if (!raw) return defaultMsg;
  try {
    const data = JSON.parse(raw);
    if (data && typeof data === "object") {
      if (data.message && data.message !== "No message available") return data.message;
      if (typeof data.trace === "string") {
        const m = data.trace.match(/ResponseStatusException:\s*\d+\s+[A-Z_]+\s+"(.+)"$/m);
        if (m) return m[1];
      }
      if (data.error) return `${data.error} (${res.status})`;
    }
  } catch {
    // Not JSON — the body itself is already the plain-text message.
  }
  return raw.length > 300 ? defaultMsg : raw;
}
